import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import BackIcon from '../assets/Icons/black_X.svg';
import UploadIcon from '../assets/Icons/plus.svg';

// Our internal template fields. Admin maps the client's spreadsheet columns onto these.
const TEMPLATE_FIELDS = [
  { key: 'appointmentDate', label: 'Procedure Date', required: true, aliases: ['procedure date', 'date', 'appointment date', 'surgery date', 'exam date', 'date/time', 'date time', 'datetime', 'date-time'] },
  { key: 'medicalNumber', label: 'Medical Number / SSN', required: true, aliases: ['medical number', 'medical record number', 'ssn', 'mrn', 'patient id', 'id', 'last 4', 'acct#', 'acct #', 'account#', 'account #', 'account number', 'account', 'acct num', 'acct'] },
  { key: 'phoneNumber', label: 'Phone', required: false, aliases: ['phone', 'phone number', 'mobile', 'cell', 'cell phone', 'patient / phone', 'patient/phone', 'patient phone', 'patient mobile phone', 'mobile phone', 'home phone', 'home'] },
  { key: 'email', label: 'Email', required: false, aliases: ['email', 'e mail', 'email address', 'patient email', 'patient e mail'] },
  { key: 'doctorName', label: 'Doctor', required: true, aliases: ['doctor', 'physician', 'provider', 'md', 'doctor name'] },
  { key: 'prep', label: 'Laxative / Prep', required: false, aliases: ['laxative', 'prep', 'solution', 'medication', 'prep type'] },
  { key: 'appointmentTime', label: 'Time', required: false, aliases: ['time', 'appointment time', 'procedure time'] },
  { key: 'specialNotes', label: 'Comment', required: false, aliases: ['comment', 'notes', 'special notes', 'comments', 'activity'] },
  { key: 'location', label: 'Location', required: false, aliases: ['location', 'facility', 'site'] },
];

const MAPPING_STORAGE_PREFIX = 'gistmd_import_column_mapping:';

const normalizeHeader = (h) =>
  String(h || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, ' ').trim();

const autoMapColumns = (rawHeaders) => {
  const normalizedHeaders = rawHeaders.map(normalizeHeader);
  const mapping = {};
  TEMPLATE_FIELDS.forEach(field => {
    let idx = normalizedHeaders.findIndex(h => field.aliases.includes(h));
    if (idx === -1) {
      idx = normalizedHeaders.findIndex(h => field.aliases.some(a => h.includes(a)));
    }
    mapping[field.key] = idx;
  });
  return mapping;
};

// Robust CSV parser that supports line breaks inside quoted cells
const parseCSV = (input) => {
  const rows = [];
  let currentRow = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < input.length; i++) {
    const char = input[i];
    const nextChar = input[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentField += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
      currentRow.push(currentField.trim());
      rows.push(currentRow);
      currentRow = [];
      currentField = '';
    } else {
      currentField += char;
    }
  }

  currentRow.push(currentField.trim());
  rows.push(currentRow);

  return rows.filter(row => row.some(field => field !== ''));
};

const findCol = (names, header) => {
  for (const name of names) {
    const idx = header.findIndex(h => h?.toLowerCase().includes(name.toLowerCase()));
    if (idx !== -1) return idx;
  }
  return -1;
};

// Saved mappings predate the dedicated "location" field and may still map the
// Location column into the Comment field. Migrate them once on load.
const migrateSavedMapping = (mapping, header) => {
  const notesIdx = mapping.specialNotes;
  if (notesIdx === undefined || notesIdx === -1 || notesIdx === null) return mapping;
  const notesHeader = normalizeHeader(header[notesIdx]);
  if (!/^(location|facility|site)$/.test(notesHeader)) return mapping;

  const locationIdx = mapping.location;
  const hasLocation = locationIdx !== undefined && locationIdx !== -1 && locationIdx !== null;
  const notesReMap = findCol(['comment', 'notes', 'special notes', 'comments', 'activity'], header);
  return {
    ...mapping,
    location: hasLocation ? locationIdx : notesIdx,
    specialNotes: notesReMap,
  };
};

const extractPhoneFromPatientPhone = (value) => {
  if (!value) return '';
  const lines = value.split(/[\n\r]+/).filter(Boolean);
  // Prefer the line explicitly labeled as mobile (M)
  const mobileLine = lines.find(line => /\(M\)/i.test(line));
  if (mobileLine) {
    const mobileMatch = mobileLine.match(/\(\d{3}\)\s*\d{3}-\d{4}/);
    if (mobileMatch) return mobileMatch[0].replace(/[^0-9]/g, '');
    const mobileDigits = mobileLine.replace(/[^0-9]/g, '');
    if (mobileDigits.length >= 10) return mobileDigits.slice(-10);
  }
  // Fall back to the first formatted phone found, or the last 10 digits
  const formattedMatch = value.match(/\(\d{3}\)\s*\d{3}-\d{4}/);
  if (formattedMatch) return formattedMatch[0].replace(/[^0-9]/g, '');
  const digits = value.replace(/[^0-9]/g, '');
  return digits.slice(-10);
};

const splitDateTime = (value) => {
  const normalized = (value || '').replace(/[\n\r]+/g, ' ').trim();
  const parts = normalized.split(/\s+/);
  if (parts.length < 2) return { date: normalized, time: '' };
  return { date: parts[0], time: parts.slice(1).join(' ') };
};

const cleanEmail = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (trimmed === '-' || trimmed.toUpperCase() === '#N/A' || trimmed.toUpperCase() === 'N/A') return '';
  return trimmed;
};

export default function CSVUploadPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [hospitals, setHospitals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [staffMembers, setStaffMembers] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [prepOptions, setPrepOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [file, setFile] = useState(null);
  const [rawHeaders, setRawHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [csvData, setCsvData] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [detectedFormat, setDetectedFormat] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [forceNewFormat, setForceNewFormat] = useState(false);

  const normalizeDoctorName = (doctorName) => {
    if (!doctorName) return '';

    let name = doctorName.toLowerCase().replace(/^dr\.?\s*/, '').trim();
    if (name.includes(',')) {
      const parts = name.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length >= 2) {
        name = `${parts[1]} ${parts[0]}`;
      }
    }

    return name.replace(/\s+/g, ' ').trim();
  };

  useEffect(() => {
    fetchHospitals();
  }, []);

  useEffect(() => {
    if (selectedHospital) {
      fetchDepartments(selectedHospital);
      fetchDoctors(selectedHospital);
    } else {
      setDepartments([]);
      setSelectedDepartment('');
      setDoctors([]);
      setSelectedOperator('');
    }
  }, [selectedHospital]);

  useEffect(() => {
    if (selectedDepartment) {
      fetchStaffMembers(selectedDepartment);
    } else {
      setStaffMembers([]);
      setSelectedOperator('');
    }
  }, [selectedDepartment]);

  const fetchHospitals = async () => {
    try {
      const { data } = await axios.get('/api/admin/hospitals');
      if (data.success) {
        setHospitals(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch hospitals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async (hospitalId) => {
    try {
      const { data } = await axios.get(`/api/admin/hospitals/${hospitalId}`);
      if (data.success && data.data.Departments) {
        setDepartments(data.data.Departments);
      }
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchStaffMembers = async (departmentId) => {
    try {
      const { data } = await axios.get(`/api/admin/departments/${departmentId}/staff`);
      if (data.success) {
        setStaffMembers(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch staff members:', error);
    }
  };

  const fetchDoctors = async (hospitalId) => {
    try {
      const url = hospitalId
        ? `/api/gi/doctors?hospitalId=${encodeURIComponent(hospitalId)}`
        : '/api/gi/doctors';
      const { data } = await axios.get(url);
      if (data) {
        setDoctors(data);
        // Extract all unique prep options
        const allPreps = new Set();
        data.forEach(doctor => {
          doctor.medicationGroups?.forEach(group => {
            group.options?.forEach(opt => allPreps.add(opt));
          });
        });
        setPrepOptions(Array.from(allPreps).sort());
      }
    } catch (error) {
      console.error('Failed to fetch doctors:', error);
    }
  };

  const validateRow = (row, rowIndex) => {
    const warnings = [];
    const doctorNames = doctors.map(d => normalizeDoctorName(d.name));

    // Validate doctor (case-insensitive, handles "LastName, FirstName" format)
    if (row.doctorName) {
      const inputNormalized = normalizeDoctorName(row.doctorName);

      // Also try reversed order (system stores "LastName FirstName")
      const parts = inputNormalized.split(' ');
      const reversed = parts.length === 2 ? `${parts[1]} ${parts[0]}` : '';
      
      if (!doctorNames.includes(inputNormalized) && !doctorNames.includes(reversed)) {
        warnings.push({
          row: rowIndex + 2,
          field: 'Doctor',
          value: row.doctorName,
          message: `"${row.doctorName}" is not a recognized doctor`
        });
      }
    }

    return warnings;
  };

  const getDefaultPrepForDoctor = (doctorName) => {
    if (!doctorName || doctors.length === 0) return '';

    const normalize = (name) => {
      const normal = normalizeDoctorName(name);
      const parts = normal.split(' ').filter(Boolean);
      const reversed = parts.length === 2 ? `${parts[1]} ${parts[0]}` : '';
      return { normal, reversed };
    };

    const input = normalize(doctorName);
    const doctor = doctors.find(d => {
      const doc = normalize(d.name);
      return doc.normal === input.normal || doc.normal === input.reversed;
    });

    if (!doctor) return '';

    return (doctor.medicationGroups || [])
      .map(group => group.default)
      .filter(Boolean)
      .join(',');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setRawHeaders([]);
    setRawRows([]);
    setColumnMapping({});
    setCsvData([]);
    setResults(null);
    setValidationWarnings([]);
    setDetectedFormat(null);
    setDebugInfo(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const fileName = selectedFile.name.toLowerCase();
        const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
        let rows = [];

        if (isExcel) {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          // header: 1 -> array-of-arrays; raw: false -> format dates/numbers as displayed strings
          rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: '' });
        } else {
          const decoder = new TextDecoder('utf-8');
          const text = decoder.decode(event.target.result).replace(/^\uFEFF/, '');
          rows = parseCSV(text);
        }

        if (rows.length < 2) {
          setParseError('File is empty or has no data rows');
          return;
        }

        const header = rows[0].map(h => String(h ?? '').trim());
        const dataRows = rows.slice(1).filter(row => row.some(cell => String(cell ?? '').trim() !== ''));

        // Detect format label for debug display only (mapping UI is the source of truth)
        const dateTimeIdx = findCol(['date/time', 'date time', 'datetime', 'date-time'], header);
        const acctIdx = findCol(['acct#', 'acct #', 'account#', 'account #', 'account number', 'account', 'acct num', 'acct'], header);
        const formatLabel = forceNewFormat || (dateTimeIdx !== -1 && acctIdx !== -1)
          ? 'new Excel export'
          : 'legacy CSV';
        setDetectedFormat(formatLabel);

        // Try to reuse a previously saved mapping for this exact set of headers
        const mappingKey = MAPPING_STORAGE_PREFIX + JSON.stringify(header.map(normalizeHeader));
        let mapping;
        try {
          const saved = localStorage.getItem(mappingKey);
          mapping = saved ? JSON.parse(saved) : null;
        } catch {
          mapping = null;
        }
        if (!mapping) {
          mapping = autoMapColumns(header);
        } else {
          mapping = migrateSavedMapping(mapping, header);
        }

        setRawHeaders(header);
        setRawRows(dataRows);
        setColumnMapping(mapping);
        setDebugInfo({ mapping });
        console.log('Detected columns:', header, 'Auto-mapped as:', mapping);
      } catch (error) {
        console.error('Error parsing file:', error);
        setParseError('Failed to parse file: ' + error.message);
      }
    };

    reader.onerror = () => {
      setParseError('Failed to read file');
    };

    reader.readAsArrayBuffer(selectedFile);
  };

  const handleMappingChange = (fieldKey, headerIndex) => {
    setColumnMapping(prev => {
      const next = { ...prev, [fieldKey]: headerIndex };
      if (rawHeaders.length) {
        const mappingKey = MAPPING_STORAGE_PREFIX + JSON.stringify(rawHeaders.map(normalizeHeader));
        try {
          localStorage.setItem(mappingKey, JSON.stringify(next));
        } catch {
          // ignore storage errors (e.g. private browsing)
        }
      }
      return next;
    });
  };

  // Rebuild the internal patient rows whenever the raw data or the column mapping changes
  useEffect(() => {
    if (rawRows.length === 0 || Object.keys(columnMapping).length === 0) {
      setCsvData([]);
      setValidationWarnings([]);
      return;
    }

    const getValue = (row, fieldKey) => {
      const idx = columnMapping[fieldKey];
      if (idx === undefined || idx === -1 || idx === null) return '';
      return String(row[idx] ?? '').trim();
    };

    const dataRows = [];
    const allWarnings = [];

    rawRows.forEach((row, i) => {
      // Skip repeated header rows
      const firstCell = String(row[0] ?? '').trim().toLowerCase();
      if (firstCell.includes('procedure date') ||
          firstCell.includes('date/time') ||
          firstCell.includes('last 4') ||
          firstCell.includes('ssn')) return;

      const medicalNumber = getValue(row, 'medicalNumber');
      const rawPhone = getValue(row, 'phoneNumber');
      const rawEmail = getValue(row, 'email');
      const doctorName = getValue(row, 'doctorName');
      const rawPrep = getValue(row, 'prep');
      let rawDate = getValue(row, 'appointmentDate');
      let rawTime = getValue(row, 'appointmentTime');
      const rawNotes = getValue(row, 'specialNotes');
      const rawLocation = getValue(row, 'location');
      // Excel "Location" cells often contain newlines from libre/office exports
      const location = rawLocation.replace(/[\n\r]+/g, ' ').replace(/\s{2,}/g, ' ').trim();

      // SSN last 4
      const digitsOnly = medicalNumber.replace(/[^0-9]/g, '');
      const ssnLast4 = digitsOnly.length >= 4 ? digitsOnly.slice(-4) : medicalNumber.slice(-4);

      // Phone extraction (handles multi-line Patient / Phone cells)
      const phoneNumber = extractPhoneFromPatientPhone(rawPhone);

      // Email cleaning (dash / #N/A -> empty)
      const email = cleanEmail(rawEmail);

      // When a single column contains both date and time, split it
      let appointmentDate = rawDate;
      let appointmentTime = rawTime;
      if (!appointmentTime && appointmentDate && /[\s:]/.test(appointmentDate)) {
        const split = splitDateTime(appointmentDate);
        appointmentDate = split.date;
        appointmentTime = split.time;
      }

      // Prep: if no prep column is mapped, fall back to the doctor's default
      const prepIdx = columnMapping.prep;
      const prep = (prepIdx === undefined || prepIdx === -1 || prepIdx === null)
        ? getDefaultPrepForDoctor(doctorName)
        : rawPrep;

      const patientRow = {
        ssnLast4,
        phoneNumber,
        email,
        doctorName,
        prep,
        specialNotes: rawNotes,
        location,
        appointmentDate,
        appointmentTime,
      };

      const rowWarnings = validateRow(patientRow, i);
      allWarnings.push(...rowWarnings);

      dataRows.push(patientRow);
    });

    setCsvData(dataRows);
    setValidationWarnings(allWarnings);
    if (dataRows.length > 0) {
      setDebugInfo(prev => ({ ...prev, firstRow: dataRows[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRows, columnMapping, doctors]);

  const handleUpload = async () => {
    if (!selectedHospital || !selectedDepartment) {
      alert('Please select hospital and department');
      return;
    }

    if (csvData.length === 0) {
      alert('No valid data to upload');
      return;
    }

    setUploading(true);
    setResults(null);

    try {
      const { data } = await axios.post('/api/admin/bulk-import', {
        hospitalId: selectedHospital,
        departmentId: selectedDepartment,
        staffMemberId: selectedOperator || undefined,
        patients: csvData,
        autoAssignOperator: true
      });

      setResults(data);
    } catch (error) {
      console.error('Upload failed:', error);
      const responseData = error.response?.data;
      setResults({
        success: false,
        error: responseData?.error || responseData?.message || 'Upload failed',
        errors: responseData?.errors,
        warnings: responseData?.warnings,
        created: responseData?.created,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template = `Procedure Date,Medical Number,Phone,Email,Doctor,Laxative,Time,Comment
6/10/2026,B2586200,(401) 578-5555,patient@email.com,"Newton, Eric",Miralax_Dulcolax,8:00:00 AM,BAYSIDE ENDOSCOPY CENTER
6/12/2026,B1234567,(401) 555-1234,patient2@email.com,"Margolis, Peter",Plenvu,9:00:00 AM,Notes here`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'patient_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <Container>Loading...</Container>;

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/admin')}>
          ← Back to Admin
        </BackButton>
        <Title>Bulk Patient Import</Title>
        <TemplateButton onClick={handleDownloadTemplate}>
          Download Template
        </TemplateButton>
      </Header>

      <Content>
        <Section>
          <SectionTitle>1. Select Location</SectionTitle>
          
          <FormGrid>
            <FormField>
              <label>Hospital / Facility *</label>
              <Select
                value={selectedHospital}
                onChange={e => setSelectedHospital(e.target.value)}
              >
                <option value="">Select a hospital...</option>
                {hospitals.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </Select>
            </FormField>

            <FormField>
              <label>Department *</label>
              <Select
                value={selectedDepartment}
                onChange={e => setSelectedDepartment(e.target.value)}
                disabled={!selectedHospital}
              >
                <option value="">Select a department...</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Select>
            </FormField>

            <FormField>
              <label>Fallback Operator (optional - auto-assigned by doctor)</label>
              <Select
                value={selectedOperator}
                onChange={e => setSelectedOperator(e.target.value)}
                disabled={!selectedDepartment}
              >
                <option value="">Auto-assign by doctor mapping</option>
                {staffMembers.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.email})</option>
                ))}
              </Select>
            </FormField>
          </FormGrid>
        </Section>

        <Section>
          <SectionTitle>2. Upload File</SectionTitle>
          
          <UploadArea 
            onClick={() => fileInputRef.current?.click()}
            $hasFile={!!file}
          >
            <UploadIconWrapper>
              <img src={UploadIcon} alt="" />
            </UploadIconWrapper>
            <UploadText>
              {file ? file.name : 'Click to select an Excel or CSV file'}
            </UploadText>
            <UploadHint>
              Upload an Excel or CSV file. Any column names are fine — you'll map them to our fields next.
              Supported formats: legacy CSV (Procedure Date, Medical Number, Phone, Email, Doctor, Laxative, Time, Comment)
              or new Excel export (Date/Time, Duration, Patient / Phone, Acct#, Activity, Notes, Provider, Location, Status Total, Patient Email).
            </UploadHint>

            <TemplateGuidelines>
              <GuidelineTitle>Required columns for new Excel format</GuidelineTitle>
              <GuidelineList>
                <li><strong>Date/Time</strong> — appointment date and time (e.g. 8/10/2026 07:00 AM). Can be on one line or split across lines inside the cell.</li>
                <li><strong>Patient / Phone</strong> — patient contact phone. Example: <code>(M) (401) 529-8776</code></li>
                <li><strong>Acct#</strong> — patient account number. The last 4 digits are extracted automatically.</li>
                <li><strong>Provider</strong> — doctor name.</li>
                <li>Optional: <strong>Activity, Notes, Location, Patient Email</strong></li>
              </GuidelineList>
              <GuidelineNote>The old format still works: Procedure Date, Medical Number, Phone, Email, Doctor, Laxative, Time, Comment.</GuidelineNote>
            </TemplateGuidelines>

            <FormatToggle>
              <input
                type="checkbox"
                id="forceNewFormat"
                checked={forceNewFormat}
                onChange={e => setForceNewFormat(e.target.checked)}
              />
              <label htmlFor="forceNewFormat">Force new Excel format (Date/Time + Acct#)</label>
            </FormatToggle>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </UploadArea>

          {parseError && (
            <ErrorMessage>{parseError}</ErrorMessage>
          )}

          {rawHeaders.length > 0 && (
            <MappingSection>
              <PreviewTitle>Map columns from your file to our fields</PreviewTitle>
              <MappingGrid>
                {TEMPLATE_FIELDS.map(field => (
                  <MappingRow key={field.key}>
                    <MappingLabel>
                      {field.label}{field.required && <RequiredMark> *</RequiredMark>}
                    </MappingLabel>
                    <Select
                      value={columnMapping[field.key] ?? -1}
                      onChange={e => handleMappingChange(field.key, Number(e.target.value))}
                    >
                      <option value={-1}>-- Not mapped --</option>
                      {rawHeaders.map((h, idx) => (
                        <option key={idx} value={idx}>{h || `Column ${idx + 1}`}</option>
                      ))}
                    </Select>
                  </MappingRow>
                ))}
              </MappingGrid>
            </MappingSection>
          )}

          {validationWarnings.length > 0 && (
            <WarningSection>
              <WarningTitle>
                <WarningIcon>⚠️</WarningIcon>
                {validationWarnings.length} validation warning{validationWarnings.length > 1 ? 's' : ''} found
              </WarningTitle>
              <WarningList>
                {validationWarnings.slice(0, 10).map((warning, idx) => (
                  <WarningListItem key={idx}>
                    <strong>Row {warning.row}</strong> ({warning.field}): {warning.message}
                  </WarningListItem>
                ))}
                {validationWarnings.length > 10 && (
                  <WarningListItem>...and {validationWarnings.length - 10} more</WarningListItem>
                )}
              </WarningList>
              <ValidValuesHint>
                <strong>Valid Doctors:</strong> {doctors.map(d => d.name).join(', ') || 'Loading...'}
                <br />
                <strong>Valid Preps:</strong> {prepOptions.join(', ') || 'Loading...'}
              </ValidValuesHint>
            </WarningSection>
          )}

          {debugInfo && (
            <DebugSection>
              <DebugTitle>Detected format: {detectedFormat}</DebugTitle>
              <DebugRow><strong>Column mapping:</strong> {JSON.stringify(debugInfo.mapping)}</DebugRow>
              <DebugRow><strong>First parsed row:</strong> {JSON.stringify(debugInfo.firstRow, null, 2)}</DebugRow>
            </DebugSection>
          )}

          {csvData.length > 0 && (
            <PreviewSection>
              <PreviewTitle>Preview ({csvData.length} patients found)</PreviewTitle>
              <PreviewTableContainer>
              <PreviewTable>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>SSN (last 4)</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Doctor</th>
                    <th>Laxative</th>
                    <th>Location</th>
                    <th>Time</th>
                    <th>Comment</th>
                  </tr>
                </thead>
                <tbody>
                  {csvData.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.appointmentDate}</td>
                      <td>{row.ssnLast4}</td>
                      <td>{row.phoneNumber}</td>
                      <td>{row.email}</td>
                      <td>{row.doctorName}</td>
                      <td>{row.prep}</td>
                      <td>{row.location}</td>
                      <td>{row.appointmentTime}</td>
                      <td>{row.specialNotes}</td>
                    </tr>
                  ))}
                </tbody>
              </PreviewTable>
              </PreviewTableContainer>
              {csvData.length > 10 && (
                <PreviewNote>Scroll inside the table to see all {csvData.length} rows.</PreviewNote>
              )}
            </PreviewSection>
          )}
        </Section>

        <Section>
          <SectionTitle>3. Import</SectionTitle>
          <UploadButton
            onClick={handleUpload}
            disabled={!selectedHospital || !selectedDepartment || csvData.length === 0 || uploading}
          >
            {uploading ? 'Importing...' : `Import ${csvData.length} Patients`}
          </UploadButton>

          {results && (
            <ResultsSection>
              {results.success ? (
                <SuccessResult>
                  <ResultTitle>Import Successful!</ResultTitle>
                  <ResultStats>
                    <StatRow>
                      <StatLabel>Created:</StatLabel>
                      <StatValue success>{results.created || 0}</StatValue>
                    </StatRow>
                    <StatRow>
                      <StatLabel>Errors:</StatLabel>
                      <StatValue error>{results.errors?.length || 0}</StatValue>
                    </StatRow>
                  </ResultStats>
                  {results.warnings?.length > 0 && (
                    <WarningsList>
                      <WarningHeader>⚠️ {results.warnings.length} Warning{results.warnings.length > 1 ? 's' : ''}</WarningHeader>
                      {results.warnings.slice(0, 10).map((warn, idx) => (
                        <WarningItem key={idx}>
                          <strong>Row {warn.row}</strong> ({warn.field}): {warn.message}
                        </WarningItem>
                      ))}
                      {results.warnings.length > 10 && (
                        <WarningItem>...and {results.warnings.length - 10} more</WarningItem>
                      )}
                    </WarningsList>
                  )}
                  {results.errors?.length > 0 && (
                    <ErrorsList>
                      {results.errors.map((err, idx) => (
                        <ErrorItem key={idx}>Row {err.row}: {err.error}</ErrorItem>
                      ))}
                    </ErrorsList>
                  )}
                </SuccessResult>
              ) : (
                <ErrorResult>
                  <ResultTitle>Import Failed</ResultTitle>
                  <ErrorMessage>{results.error}</ErrorMessage>
                  {results.errors?.length > 0 && (
                    <ErrorsList>
                      {results.errors.map((err, idx) => (
                        <ErrorItem key={idx}>Row {err.row}: {err.error}</ErrorItem>
                      ))}
                    </ErrorsList>
                  )}
                </ErrorResult>
              )}
            </ResultsSection>
          )}
        </Section>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  background: #f5f5f5;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e5e5e5;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const TemplateButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #84a4fc;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #4A90E2;
  }
`;

const Content = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1.5rem 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const FormField = styled.div`
  label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.5rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #84a4fc;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const UploadArea = styled.div`
  border: 2px dashed ${props => props.$hasFile ? '#84a4fc' : '#e5e5e5'};
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #84a4fc;
    background: rgba(132, 164, 252, 0.05);
  }
`;

const UploadIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  margin: 0 auto 1rem;
  background: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
  }
`;

const UploadText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
`;

const UploadHint = styled.div`
  font-size: 0.85rem;
  color: #999;
`;

const FormatToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  font-size: 0.85rem;
  color: #666;

  input {
    cursor: pointer;
  }

  label {
    cursor: pointer;
    margin: 0;
  }
`;

const TemplateGuidelines = styled.div`
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  background: #f9f9f9;
  border-left: 3px solid #4a90e2;
  border-radius: 4px;
  font-size: 0.8rem;
  color: #444;
  text-align: left;
`;

const GuidelineTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #333;
`;

const GuidelineList = styled.ul`
  margin: 0;
  padding-left: 1.2rem;
  line-height: 1.5;

  li {
    margin-bottom: 0.25rem;
  }

  code {
    background: #e8e8e8;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    font-size: 0.75rem;
  }
`;

const GuidelineNote = styled.div`
  margin-top: 0.5rem;
  font-style: italic;
  color: #666;
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.9rem;
`;

const MappingSection = styled.div`
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e5e5;
`;

const MappingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const MappingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const MappingLabel = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
`;

const RequiredMark = styled.span`
  color: #f02a4c;
`;

const WarningSection = styled.div`
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const WarningTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #e65100;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WarningIcon = styled.span`
  font-size: 1.1rem;
`;

const WarningList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  font-size: 0.85rem;
  color: #333;
  max-height: 200px;
  overflow-y: auto;
`;

const WarningListItem = styled.li`
  margin-bottom: 0.5rem;
  line-height: 1.4;
`;

const ValidValuesHint = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #ffb74d;
  font-size: 0.8rem;
  color: #666;
  line-height: 1.5;
`;

const DebugSection = styled.div`
  background: #f5f5f5;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  font-family: monospace;
  font-size: 0.8rem;
  color: #333;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DebugTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #000;
`;

const DebugRow = styled.div`
  margin-bottom: 0.5rem;
`;

const PreviewSection = styled.div`
  margin-top: 1.5rem;
`;

const PreviewTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #666;
  margin-bottom: 0.75rem;
`;

const PreviewTableContainer = styled.div`
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;

  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e5e5e5;
  }

  th {
    font-weight: 600;
    color: #666;
    background: #f5f5f5;
  }

  td {
    color: #333;
  }

  tr:hover td {
    background: #f9f9f9;
  }
`;

const PreviewNote = styled.div`
  font-size: 0.85rem;
  color: #999;
  text-align: center;
  padding: 0.75rem;
  font-style: italic;
`;

const UploadButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: #f02a4c;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #d4223f;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ResultsSection = styled.div`
  margin-top: 1.5rem;
  padding: 1.5rem;
  border-radius: 8px;
`;

const SuccessResult = styled.div`
  background: #e8f5e9;
  padding: 1.5rem;
  border-radius: 8px;
`;

const ErrorResult = styled.div`
  background: #ffebee;
  padding: 1.5rem;
  border-radius: 8px;
`;

const ResultTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
`;

const ResultStats = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatLabel = styled.span`
  font-size: 0.9rem;
  color: #666;
`;

const StatValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.success ? '#2e7d32' : props.error ? '#c62828' : '#333'};
`;

const ErrorsList = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
`;

const ErrorItem = styled.div`
  font-size: 0.85rem;
  color: #c62828;
  padding: 0.25rem 0;
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const WarningsList = styled.div`
  background: #fff3e0;
  border: 1px solid #ffb74d;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  max-height: 200px;
  overflow-y: auto;
`;

const WarningHeader = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #e65100;
  margin-bottom: 0.5rem;
`;

const WarningItem = styled.div`
  font-size: 0.85rem;
  color: #333;
  padding: 0.25rem 0;
  border-bottom: 1px solid #ffe0b2;

  &:last-child {
    border-bottom: none;
  }
`;
